import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useLanguage, translations } from "@/i18n/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";

interface NavbarProps {
  onHealthCheck: () => void;
}

const Navbar = ({ onHealthCheck }: NavbarProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useLanguage();

  const links = [
    { label: t(translations.nav.services), href: "#services" },
    { label: t(translations.nav.caseStudies), href: "#case-studies" },
    { label: t(translations.nav.about), href: "#about" },
    { label: t(translations.nav.contact), href: "#contact" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-xl">
      {/* Regulatory Alert Bar */}
      <div className="border-b border-border px-4 py-2.5">
        <p className="text-center text-xs text-muted-foreground tracking-widest uppercase">
          <span className="text-primary font-medium">{t(translations.nav.alert)}</span>{" "}
          {t(translations.nav.alertText)}
        </p>
      </div>

      <div className="container mx-auto flex items-center justify-between px-6 py-5">
        <a href="#" className="text-foreground font-semibold text-lg tracking-tight" style={{ fontFamily: "'Geist Sans', 'Inter', system-ui, sans-serif" }}>
          Lloyd Consulting AB
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-10">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300 tracking-widest uppercase"
            >
              {link.label}
            </a>
          ))}
          <Button variant="navy-outline" size="sm" onClick={onHealthCheck} className="text-xs tracking-wider uppercase">
            {t(translations.nav.healthCheck)}
          </Button>
          <LanguageToggle />
        </div>

        {/* Mobile toggle */}
        <div className="md:hidden flex items-center gap-3">
          <LanguageToggle />
          <button
            className="text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden border-t border-border bg-background px-6 py-6 space-y-5"
        >
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="block text-xs text-muted-foreground hover:text-primary tracking-widest uppercase"
              onClick={() => setMobileOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <Button variant="navy-outline" size="sm" className="w-full text-xs tracking-wider uppercase" onClick={() => { onHealthCheck(); setMobileOpen(false); }}>
            {t(translations.nav.healthCheck)}
          </Button>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar;
